// Single entry point for removing one photo from a camera question's value --
// card #4744. Every removal surface (the question's thumbnail strip, the
// full-screen viewer, and the in-app camera sheet's pending-capture list)
// routes through this so none of them can drop the local queued copy or the
// server Image independently and skip a step.
//
// This fixes the "LOCAL COPY GAP": before this card, the camera sheet's own
// removal (field_photo_camera_controller.js#done, via _setQuestionValue) set
// question.value directly and never discarded the queued copy, so a removed
// photo stayed banked in IndexedDB (and in My Photos) even though it was
// gone from the answer. result_editor_controller.js's onClearFiles already
// did this correctly for SurveyJS's own clear-files flow (discardByUrls) --
// this module generalises that one correct path for every caller.
//
// Always does the same three things, in order:
//   1. Updates the question's value (removes the matching entry)
//   2. Discards the local queued/banked copy (ADR-0048 -- an explicit tasker
//      delete is the one case that's allowed to drop an unconfirmed copy)
//   3. If the entry has a server copy (its url isn't a local blob:), deletes
//      the server Image too -- fire-and-forget: deleteServerPhoto queues an
//      offline tombstone itself when the request can't go right now.
import { discardByUrls } from "../field_image_queue"
import { deleteServerPhoto } from "../field_photo_delete_sync"

// A server photo URL is never blob: -- that always means a local/offline
// capture still waiting to upload. Matches the same test field_image_queue.js
// already uses (discardByUrls, _photoInfo) to tell local vs server apart.
export function isServerPhotoUrl(url) {
  return typeof url === "string" && !url.startsWith("blob:")
}

function entryUrl(entry) {
  return typeof entry === "string" ? entry : entry?.content
}

// Removes `url` from `question`'s value. Returns false (does nothing else)
// if the url wasn't actually in the value -- callers can use that to avoid
// firing the dependent cleanup steps twice.
export async function removePhotoFromQuestion(question, url, { csrfToken } = {}) {
  if (!question || !url) return false

  const value = Array.isArray(question.value) ? question.value : []
  const remaining = value.filter(entry => entryUrl(entry) !== url)
  if (remaining.length === value.length) return false

  question.value = remaining.length ? remaining : undefined

  await discardRemovedPhotoUrls([url], { csrfToken })

  return true
}

// Steps 2-3 only (discard local, delete server) -- for a caller that already
// did step 1 itself (updated the question's value), rather than removing one
// url at a time through removePhotoFromQuestion above.
//
// field_photo_camera_controller.js#done is the one caller: the in-app camera
// sheet rewrites the question's value as ONE batch (kept-existing entries),
// not one removal at a time, so it can't call removePhotoFromQuestion per
// url without re-deriving (and racing) the value array itself. This is the
// "LOCAL COPY GAP" fix (card #4744) -- before this, that rewrite dropped a
// photo from the answer but left its queued copy banked forever, because
// nothing ever called discardByUrls for it.
export async function discardRemovedPhotoUrls(urls, { csrfToken } = {}) {
  const list = (urls || []).filter(Boolean)
  if (!list.length) return

  discardByUrls(list).catch(() => {})

  for (const url of list) {
    if (isServerPhotoUrl(url)) deleteServerPhoto(url, csrfToken).catch(() => {})
  }
}
